/* Keep <html lang> truthful.
 *
 * index.html hard-codes lang="en" and nothing ever changed it, so a Finnish
 * user was served a document that declared itself English. That is not
 * cosmetic:
 *
 *   - a screen reader picks its voice and pronunciation rules from this
 *     attribute, so Finnish was read aloud with English phonetics;
 *   - `hyphens: auto` needs the language to know where a word may break, so
 *     Finnish headings wrapped mid-word ("Kromimolyb / deeni") — the browser
 *     was applying English rules to Finnish compounds;
 *   - a browser offers to translate a page it believes is in another language.
 *
 * Note i18n.ts's `detection.order` already lists "htmlTag": the app reads this
 * attribute but never kept it up to date. That asymmetry is what this fixes.
 *
 * Its own module rather than a few lines inside i18n.ts because importing that
 * file starts the HTTP backend fetching catalogs, which a unit test cannot do.
 */

/** The minimum an i18n instance has to expose for us to follow its language. */
export interface LanguageSource {
  resolvedLanguage?: string;
  language?: string;
  on(event: "languageChanged", handler: (lng: string) => void): void;
}

/**
 * Write `lng` onto <html lang>. Falls back to what the instance resolved,
 * which under `load: "languageOnly"` is a bare subtag ("fi") — the right thing
 * for the attribute, since a region we hold no catalog for would be a lie.
 */
export function syncDocumentLanguage(source: LanguageSource, lng?: string): void {
  if (typeof document === "undefined") return;
  const resolved = lng || source.resolvedLanguage || source.language;
  if (resolved) document.documentElement.lang = resolved;
}

/** Set it now, and keep it in step with every later change. */
export function attachDocumentLanguage(source: LanguageSource): void {
  syncDocumentLanguage(source);
  source.on("languageChanged", (lng) => syncDocumentLanguage(source, lng));
}
